/**
 * Platform Detection Utilities
 * 
 * Shared utilities for detecting device platform and browser
 * Used for camera permissions, feature detection, etc.
 * 
 * Note: For basic native platform checks, use isNativePlatform() from apiConfig.ts
 * This module adds browser-specific detection (Chrome, Safari) for UI guidance.
 */

import { isNativePlatform, getPlatformName } from './apiConfig';

export interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isChrome: boolean;
  isSafari: boolean;
  isDesktop: boolean;
  isNative: boolean;
}

/**
 * Detect current platform based on user agent and Capacitor
 * Reuses isNativePlatform() from apiConfig.ts to avoid duplication
 */
export function detectPlatform(): PlatformInfo {
  const userAgent = navigator.userAgent;
  const platform = getPlatformName();
  
  // For native apps, use Capacitor's platform detection
  const isNative = isNativePlatform();
  const isIOS = isNative ? platform === 'ios' : /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = isNative ? platform === 'android' : /Android/.test(userAgent);
  
  // Browser detection (only relevant for web)
  const isChrome = /Chrome/.test(userAgent) && !/Edg/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isDesktop = !isIOS && !isAndroid;

  return { isIOS, isAndroid, isChrome, isSafari, isDesktop, isNative };
}

/**
 * Get permission guidance i18n key based on platform
 * Returns the key suffix for faceCapture.permissionXxx
 */
export function getPermissionGuidanceKey(): string {
  const { isIOS, isAndroid, isChrome, isSafari, isNative } = detectPlatform();
  
  // Native apps have different permission flow
  if (isNative) {
    if (isIOS) return 'permissionNativeIos';
    if (isAndroid) return 'permissionNativeAndroid';
  }
  
  if (isIOS) {
    if (isChrome) return 'permissionIosChrome';
    if (isSafari) return 'permissionIosSafari';
    return 'permissionIosOther';
  }
  if (isAndroid) return 'permissionAndroid';
  return 'permissionDesktop';
}
